import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ARTICLES } from "@/data/insights";
import { useTitle, useJsonLd } from "@/lib/useTitle";
import { wa } from "@/lib/brand";

const InsightArticle = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useTitle(article ? `${article.title} — GN Agro Traders` : "Insights — GN Agro Traders", article?.excerpt);
  useJsonLd("article-ld", article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { "@type": "Organization", name: "GN Agro Traders" },
    publisher: { "@type": "Organization", name: "GN Agro Traders" },
  } : null);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl text-[#004225]">Article not found</h1>
        <Link to="/insights" className="mt-4 inline-block text-[#004225] font-semibold">← Back to Insights</Link>
      </div>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div>
      <section className="bg-[#004225] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <Link to="/insights" className="inline-flex items-center gap-1.5 text-[12px] text-white/70 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" />Insights</Link>
          <div className="mt-4 text-[10px] font-eyebrow text-[#D4AF37]">{article.date} · {article.readTime} read</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl leading-tight">{article.title}</h1>
        </div>
      </section>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="article-body">
        {article.body.map((p, i) => (
          <p key={i} className="text-[15px] leading-relaxed text-[#171717]/85 mb-5">{p}</p>
        ))}
        <div className="mt-10 bg-[#F7F2E2] border border-[#D4AF37]/40 rounded-sm p-6">
          <div className="font-display text-xl text-[#004225]">Discuss your requirement with our export team</div>
          <p className="mt-2 text-[13px] text-[#171717]/75">Specifications, samples, packaging and quotations — handled per enquiry.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/request-quote" className="rounded-full btn-primary px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">Request Bulk Quotation</Link>
            <Link to={article.related.to} className="rounded-full border border-[#004225]/20 text-[#004225] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-white">{article.related.label}</Link>
            <a href={wa()} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#25D366] text-white px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest">WhatsApp</a>
          </div>
        </div>
      </article>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="text-[10px] font-eyebrow text-[#B5942D] mb-4">More guides</div>
        <div className="grid sm:grid-cols-2 gap-4">
          {others.map((a) => (
            <Link key={a.slug} to={`/insights/${a.slug}`} className="bg-white border border-[#004225]/10 rounded-sm p-5 card-elevate">
              <div className="font-display text-lg text-[#004225] leading-snug">{a.title}</div>
              <div className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-[#004225]/70 inline-flex items-center gap-1">Read <ArrowRight className="h-3 w-3" /></div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default InsightArticle;
